import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

/**
 * Clears the "OAuth in-flight" state when the user backs out of native sign-in.
 *
 * On native, `startOAuth` opens the provider in an in-app browser while the
 * signup/login page stays mounted underneath. If the user dismisses that browser
 * instead of finishing, nothing would otherwise re-enable the disabled "Continue
 * with Google/Apple" buttons. The `browserFinished` event fires on dismissal.
 *
 * This is native-only on purpose. On web, OAuth is a full-page navigation, so the
 * page either leaves (nothing to reset) or, on a back-button return, reloads fresh.
 * A web `visibilitychange` reset would also misfire during the pre-redirect network
 * await if the user tab-switches, re-enabling the button mid-flight.
 *
 * A successful native sign-in deep-links to `/auth/callback` and unmounts this page,
 * so the cleanup's `cancelled` guard prevents a stray state update there.
 */
export function useResetOAuthInFlight(reset: () => void): void {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    const listenerPromise = Browser.addListener('browserFinished', () => {
      if (!cancelled) reset();
    });

    return () => {
      cancelled = true;
      listenerPromise.then((listener) => listener.remove());
    };
  }, [reset]);
}
